/**
 * Page which creates a webhook, associated to an API key.
 *
 * The API key must passed as a location state via:
 *
 * ```tsx
 * navigate(<url>, {
 *   state: { apiKeyId: <api_key_id> },
 * })
 * ```
 *
 * So it doesn't appear in the URL, and is read with `useLocation()`.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */
import { API, fetchApiKeyList, requestCreateWebhook } from '@/api'
import { AppRouteUrl } from '@/AppRoute'
import ActionButton from '@/components/Button/ActionButton'
import { fetchAuthSession } from 'aws-amplify/auth'
import { Field, Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { BiLoaderAlt } from 'react-icons/bi'
import { FaCircleArrowRight } from 'react-icons/fa6'
import { IoIosCopy, IoMdCheckmark } from 'react-icons/io'
import { MdRefresh } from 'react-icons/md'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import './CreateWebhookPage.css'

export const CreateWebhookPage = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const apiKeyId: string | undefined = state?.apiKeyId

  const [copied, setCopied] = useState(false)
  const [testing, setTesting] = useState(false)
  const [status, setStatus] = useState(undefined)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false)
      }, 1000)
    }
  }, [copied])

  const handleSubmit = async (name: string, url: string, secret: string) => {
    if (!testing && !loading) {
      setLoading(true)
      await requestCreateWebhook(name, url, apiKeyId, secret)
      await fetchApiKeyList(true)
      navigate(AppRouteUrl.ApiKey)
    }
  }

  const onTestClick = async (url: string, secret: string) => {
    if (!testing && !loading) {
      setTesting(true)
      setStatus(undefined)
      const { data } = await API(await fetchAuthSession()).post(
        '/webhook/test',
        secret.length > 0 ? { url, secret } : { url }
      )
      setStatus(data?.status)
      setTesting(false)
    }
  }

  return (
    <div className="page create-webhook-page">
      <h3>Create new subscription</h3>
      <Formik
        onSubmit={(props) =>
          handleSubmit(props['name'], props['url'], props['secret'])
        }
        initialValues={{ name: '', url: '', secret: '' }}
      >
        {(props) => (
          <Form>
            <label htmlFor="name" className="form-field">
              <div>Webhook name</div>
              <Field
                type="text"
                id="name"
                name="name"
                className="ircam-input"
              />
            </label>
            <label htmlFor="url" className="form-field">
              <div>Callback URL</div>
              <Field type="text" id="url" name="url" className="ircam-input" />
            </label>
            <div className="test-row">
              <ActionButton
                id="button-test"
                disabled={
                  props.values['url'].length === 0 || loading || testing
                }
                onClick={() =>
                  onTestClick(props.values['url'], props.values['secret'])
                }
              >
                {testing ? (
                  <BiLoaderAlt className="loadingIcon" />
                ) : (
                  <FaCircleArrowRight />
                )}
                Test
              </ActionButton>
              <p
                className={`test-status${
                  status === 'SUCCESS'
                    ? ' ok'
                    : status === 'ERROR'
                      ? ' error'
                      : ''
                }`}
              >
                {status ?? ''}
              </p>
            </div>
            <label htmlFor="secret" className="form-field">
              <div>Secret</div>
              <Field
                type="text"
                id="secret"
                name="secret"
                className="ircam-input"
              />
            </label>
            <div className="secret-actions">
              <ActionButton
                id="button-generate-secret"
                disabled={loading || testing}
                onClick={() => props.setFieldValue('secret', uuidv4())}
              >
                <MdRefresh />
                Generate new secret
              </ActionButton>
              <ActionButton
                id="button-copy"
                disabled={props.values['secret'].length === 0}
                onClick={() => {
                  navigator.clipboard.writeText(props.values['secret'])
                  setCopied(true)
                }}
              >
                {copied ? <IoMdCheckmark /> : <IoIosCopy />}
                Copy
              </ActionButton>
            </div>
            <div className="form-actions">
              <Link to={-1 as any} className={loading ? 'disabled' : ''}>
                <div className="button button-large button-light">
                  <div className="button button-label cancel-label">Cancel</div>
                </div>
              </Link>
              <button
                type="submit"
                className="button button-large button-dark"
                disabled={
                  loading ||
                  props.values['name'].length === 0 ||
                  props.values['url'].length === 0
                }
              >
                <div className="button button-label">
                  {loading ? 'Loading...' : 'Save new subscription'}
                </div>
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
